/**
 * shouldAutoOpenCommentSidebar
 *
 * Pure decision function for the "open the comment sidebar on document load"
 * effect in EditorContainer.
 *
 * Background — SW-8335 / Dean's bug report:
 *   "Even if you have resolved all comments and close the comment tab, the next
 *    time you open the document, the comment section is back expanded again.
 *    [...] simply remain closed if you have closed that comments section is my
 *    vote."
 *
 * Before this fix the auto-open ran on every document load whenever `comments
 * > 0`, ignoring whether the user had explicitly closed the sidebar. We now
 * persist a per-document "user-closed" flag in projectStore and gate the
 * auto-open on it.
 *
 * The decision is intentionally simple:
 *   - If the user previously closed the sidebar for this document → stay closed.
 *   - If a chat-deeplink open is already pending → let chat handle it (return false).
 *   - Otherwise auto-open iff there is at least one comment to show.
 *
 * A brand-new document with no prior interaction and no comments stays closed
 * by default — the user can open it via the comment-count badge (SW-8214).
 */

export interface ShouldAutoOpenCommentSidebarInput {
  documentId: string | null | undefined;
  commentCount: number;
  /**
   * Map of documentId → true when the user has explicitly closed the sidebar
   * for that document. Persisted to localStorage via projectStore.partialize.
   */
  userClosedSidebarByDocId: Record<string, boolean> | undefined;
  /**
   * True when a chat-driven comment navigation is queued for this document.
   * Chat handles its own open sequence — we must not race it.
   */
  hasPendingChatCommentNavigation: boolean;
}

export function shouldAutoOpenCommentSidebar(input: ShouldAutoOpenCommentSidebarInput): boolean {
  const { documentId, commentCount, userClosedSidebarByDocId, hasPendingChatCommentNavigation } = input;

  if (!documentId) return false;
  if (hasPendingChatCommentNavigation) return false;
  if (!Number.isFinite(commentCount) || commentCount <= 0) return false;

  const userClosed = userClosedSidebarByDocId?.[documentId] === true;
  if (userClosed) return false;

  return true;
}
